import Vuex from 'vuex'

import Config from '@/app.config'

import Project from '@/models/project'
import Property from '@/models/property'

import PlanBuilderManager from '@/plan-builder-manager'
import PropertyManager from '@/property-manager'
import Logger from '@/logger'

const loggingCategoryFlow = 'Renovation Lifecycle'

export default {

    computed: {

        ...Vuex.mapState({
            /**
             * @param {VuexState} stateStore
             * @returns {Project}
             */
            currentProject: stateStore => Project.ensure(stateStore.improvements.currentProject),

            /**
             * @param {VuexState} stateStore
             * @returns {Property}
             */
            property: stateStore => stateStore.property ? Property.ensure(stateStore.property) : null,

            /**
             * @param {VuexState} stateStore
             * @returns {boolean}
             */
            loadFailed: stateStore => stateStore.improvements.loadFailed,

            /**
             * @param {VuexState} stateStore
             * @returns {Array<number>}
             */
            notAppliedImprovements: stateStore => stateStore.metrics.notAppliedImprovements,

            /**
             * @param {VuexState} stateStore
             * @returns {Array}
             */
            savings: stateStore => stateStore.improvements.savings,
        }),

        /**
         * @returns {boolean}
         */
        calculated() {
            return this.property ? this.property.calculated : false
        },

        /**
         * @returns {boolean}
         */
        hasProjectToLoad() {
            return !!(this.currentProject.budget && this.currentProject.motivation)
        },

        query() {
            return this._getQuery()
        },
    },

    data() {
        return {
            initialised: false,

            loading: false,

            /**
             * @type {?string}
             */
            message: null,

            name: 'Plan Loader',
        }
    },

    watch: {

        async calculated(newValue) {
            if (newValue && this.hasProjectToLoad && !this.initialised) await this._loadProjectAsync()
        },

        async hasProjectToLoad(newValue) {
            if (newValue && this.calculated && !this.initialised) await this._loadProjectAsync()
        },

        notAppliedImprovements(newValue) {
            const newMessage = newValue && newValue.length
                ? newValue.length > 1
                    ? `Some improvements are incompatible with other options that you have selected and has been ignored when calculating your savings.`
                    : `${ newValue[0].title } is incompatible with other options that you have selected and has been ignored when calculating your savings.`
                : null

            if (this.message !== newMessage) {
                this.message = newMessage

                if (Config.systemGeneratedErrors.incompatibleImprovements && this.message) {
                    // noinspection JSIgnoredPromiseFromCall - Intended Fire & Forget
                    PlanBuilderManager.ApiData.sendSystemGeneratedErrorReportAsync(this.$scheme.id,
                        `Incompatible improvements ignored (${ newValue.map(imp => `${ imp.title } [${ imp.id }]`).join(', ') })`)
                }
            }
        },
    },

    async mounted() {
        try {
            if (this.calculated && this.hasProjectToLoad)
                await this._loadProjectAsync()
        } catch (err) {
            throw err
        }
    },

    methods: {

        /**
         * Gets the query to send.
         * @private
         */
        _getQuery() {
            return this.property
                ? { id: this.property.id }
                : this.$route.query ? { id: this.$route.query.id } : {}
        },

        /**
         * Loads the project and supporting data to build a plan into VUEX state and creates a new renovation.
         * @return {Promise} A promise.
         * @private
         */
        async _loadProjectAsync() {
            Logger.logObject({ loading: this.loading }, 'Plan Loader triggered', loggingCategoryFlow)

            if (this.loading) // This often doesn't bail due to some oddities in the component lifecycle
                return

            try {
                this.initialised = true
                this.loading = true
                Logger.log('Plan Loader ensuring statistics', loggingCategoryFlow)
                await PropertyManager.ensureBaseStatisticsAsync(this.property.id)
                Logger.log('Plan Loader loading project', loggingCategoryFlow)
                await PlanBuilderManager.loadProjectIntoStoreAsync(this.property.id, { initialise: true })
                Logger.log('Plan Loader loading completed', loggingCategoryFlow)
                this.loading = false
            } catch (err) {
                this.$logger.error(`An error occurred trying to load the project (${ err.message }) - attempting to recalculate...`, this.name)
                Logger.log('Plan Loader attempting recalculation', loggingCategoryFlow)
                if (!this.loadFailed) {
                    try {
                        Logger.log('Plan Loader polling calculation status', loggingCategoryFlow)
                        await PlanBuilderManager.ApiData.pollCalculationStatusAsync(this.property.id)
                    } catch (err2) {
                        this.$logger.warn(`Unable to poll for improvement calculations to recover error state (${ err2.message })`, this.name)
                        this.$router.push({
                            name: 'MyHome',
                            query: this._getQuery(),
                        })
                    }
                }
                this.loading = false
            }
        },
    },
}
